import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Newspaper, ExternalLink, Quote, BadgeCheck, MapPin, GraduationCap } from "lucide-react";
import { API } from "../App";
import { Seo, breadcrumbJsonLd, faqJsonLd, SITE_URL } from "../components/Seo";
import BusinessHeader from "../components/business/BusinessHeader";
import BusinessFooter from "../components/business/BusinessFooter";
import BusinessLeadForm from "../components/business/BusinessLeadForm";
import { Blocks, FaqBlock, CtaBlock, Section, WRAP } from "../components/business/blocks";
import { GOLD, NAVY, Aurora, Reveal, SectionHead, GoldButton } from "../components/business/ui";
import { DEFAULT_CONTENT, mergeContent } from "../components/admin/BusinessContentEditor";
import { LANGUAGES, TRANSLATIONS } from "../components/businessTranslations";
import { HOME_BLOCKS } from "../data/business/home";
import { BUSINESS, HOME_FAQ } from "../data/business/profile";
import { LatestArticleBar } from "../components/viden/DbArticleList";
import { API_TIMEOUT } from "../utils/crawler";

const LANG_STORAGE = "votraBusinessLang";
const IMG_HERO =
  "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const IMG_HERO_SET = [640, 1024, 1440, 1920]
  .map((w) => `https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=80&w=${w} ${w}w`)
  .join(", ");

const TRUST_ROW = [
  { icon: BadgeCheck, text: `${BUSINESS.legalName} · CVR ${BUSINESS.cvr}` },
  { icon: MapPin, text: `Kontor i ${BUSINESS.office.city} · registreret i ${BUSINESS.registered.city}` },
  { icon: GraduationCap, text: "Del af Votra-koncernen, uddannet vognmænd siden 2015" },
];

const BusinessPage = () => {
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_STORAGE) || "da");
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [news, setNews] = useState([]);

  useEffect(() => {
    localStorage.setItem(LANG_STORAGE, lang);
  }, [lang]);

  useEffect(() => {
    if (lang === "da") {
      axios
        // Timeout: et hængende CMS-kald må aldrig forsinke sidens indhold eller en crawl.
        // 1500 ms for crawlere og prerendering, 4000 ms for rigtige browsere.
        .get(`${API}/business/content`, { timeout: API_TIMEOUT })
        // Gemt CMS-indhold fra Business 1.0 må ikke genopstå på 2.0-siden.
        .then((res) =>
          setContent(
            res.data?.version === DEFAULT_CONTENT.version
              ? mergeContent(DEFAULT_CONTENT, res.data)
              : DEFAULT_CONTENT
          )
        )
        .catch(() => setContent(DEFAULT_CONTENT));
    } else {
      setContent(mergeContent(DEFAULT_CONTENT, TRANSLATIONS[lang] || {}));
    }
  }, [lang]);

  useEffect(() => {
    // Nyhederne er sekundære på /business: siden skal først og fremmest handle om
    // Votra Business' egne ydelser. Derfor kun de seneste tre.
    axios.get(`${API}/business/news?limit=3`, { timeout: API_TIMEOUT })
      .then((r) => setNews(r.data || [])).catch(() => setNews([]));
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById("kontakt");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const testimonials = content.testimonials;
  const initials = (name = "") =>
    name.trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div className="votra-biz min-h-screen flex flex-col bg-white" data-testid="business-page">
      <Seo
        title="Drifts- og økonomiafdeling for vognmænd | Votra Business"
        description="Vi driver forretningen bag din vognmandsforretning: bogføring, moms, løn, fakturering, debitorstyring, controlling og fleet-administration, med egen portal."
        path="/business"
        brand="business"
        jsonLd={[
          breadcrumbJsonLd([{ name: "Forside", path: "/" }, { name: "Votra Business", path: "/business" }]),
          faqJsonLd(HOME_FAQ, `${SITE_URL}/business#faq`),
        ]}
      />
      <div className="votra-grain" aria-hidden />
      <BusinessHeader lang={lang} setLang={setLang} languages={LANGUAGES} />

      {/* Promo-stribe */}
      <div
        className="w-full text-center text-xs sm:text-sm font-medium tracking-wide px-4 py-2.5 flex items-center justify-center gap-2"
        style={{ backgroundColor: GOLD, color: NAVY }}
        data-testid="business-promo-banner"
      >
        <span>{content.promo}</span>
      </div>

      {/* ------------ HERO ------------ */}
      <main id="indhold">
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }} data-testid="business-hero">
        <img src={IMG_HERO} srcSet={IMG_HERO_SET} sizes="100vw" alt="" aria-hidden
          fetchPriority="high" decoding="async"
          className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${NAVY} 0%, rgba(11,18,32,0.94) 45%, rgba(11,18,32,0.6) 100%)` }} />
        <Aurora grid />
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ backgroundColor: GOLD }} />
        <div className={`${WRAP} relative py-24 md:py-36`}>
          <div className="max-w-3xl">
            <p className="votra-hero-rise votra-hero-rise--1 text-xs font-semibold uppercase tracking-[0.3em] mb-7" style={{ color: GOLD }}>
              {content.hero.badge}
            </p>
            <h1 className="votra-hero-rise votra-hero-rise--2 font-display text-white font-medium leading-[1.14] text-4xl sm:text-5xl lg:text-[4rem] mb-8">
              {content.hero.titleStart}{" "}
              <span className="italic votra-shimmer-text inline-block pb-1">{content.hero.titleAccent}</span>
            </h1>
            <p className="votra-hero-rise votra-hero-rise--3 text-slate-300 text-base md:text-xl leading-relaxed mb-11 max-w-2xl">
              {content.hero.subtitle}
            </p>
            <div className="votra-hero-rise votra-hero-rise--4 flex flex-col sm:flex-row gap-4">
              <GoldButton href="#kontakt" onClick={(e) => { e.preventDefault(); scrollToContact(); }} testid="hero-primary-cta">
                {content.hero.primaryCta}
              </GoldButton>
              <a href="#overtager" onClick={(e) => { e.preventDefault(); scrollTo("overtager"); }}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold border text-white border-white/30 hover:bg-white/10 transition-colors"
                data-testid="hero-secondary-cta">
                {content.hero.secondaryCta}
              </a>
              <Link to="/business/selskabsstiftelse"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold border text-white border-white/30 hover:bg-white/10 transition-colors"
                data-testid="hero-stiftelse-cta">
                {lang === "da" ? "Opret dit selskab" : "Start your company"}
              </Link>
            </div>
          </div>
        </div>

        {/* Entitets- og tillidsbjælke — kun verificerede oplysninger */}
        <div className="relative border-t border-white/10">
          <div className={`${WRAP} grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10`}>
            {TRUST_ROW.map((t, i) => (
              <div key={i} className="flex items-center gap-3 py-5 md:py-6 md:px-6 first:md:pl-0" data-testid={`hero-trust-${i}`}>
                <t.icon className="h-4 w-4 flex-shrink-0" style={{ color: GOLD }} />
                <p className="text-slate-400 text-sm leading-snug">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div id="overtager" />
      <Blocks blocks={HOME_BLOCKS} />

      {/* ------------ TRANSPORT-NYHEDER ------------ */}
      {news.length > 0 && (
        <Section tone="white" testid="transport-news-section">
          <SectionHead overline="Branchenyt" title="Transport-nyheder, samlet ét sted"
            intro="Aktuelt nyt fra transportbranchen. Korte resuméer i vores egne ord, læs den fulde artikel hos kilden."
            className="mb-12" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((n, idx) => (
              <Reveal key={n.id || idx} i={idx % 3}>
                <article className="group h-full flex flex-col rounded-2xl bg-white border border-slate-200 p-6 hover:shadow-xl hover:border-transparent transition-all duration-300 votra-glow-edge"
                  data-testid={`news-card-${idx}`}>
                  <div className="flex items-center gap-2 mb-4">
                    <Newspaper className="h-4 w-4" style={{ color: GOLD }} />
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{n.source}</span>
                  </div>
                  <h3 className="font-display text-lg font-medium text-slate-900 leading-snug mb-3">{n.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1">{n.ai_summary}</p>
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <a href={n.link} target="_blank" rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900"
                      data-testid={`news-link-${idx}`}>
                      Læs hos {n.source}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </Section>
      )}

      {/* ------------ KUNDEUDTALELSER (kun når der er verificerede udtalelser) ------------ */}
      {testimonials?.items?.length > 0 && (
        <section className="py-20 md:py-28 relative overflow-hidden" style={{ backgroundColor: NAVY }} data-testid="testimonials-section">
          <Aurora />
          <div className={`${WRAP} relative`}>
            <SectionHead light overline={testimonials.sectionLabel} title={testimonials.sectionTitle} intro={testimonials.sectionSubtitle} className="mb-14" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.items.map((it, idx) => (
                <Reveal key={idx} i={idx}>
                  <figure className="h-full flex flex-col rounded-2xl p-7 border border-white/10 bg-white/[0.03]" data-testid={`testimonial-${idx}`}>
                    <Quote className="h-8 w-8 mb-5" style={{ color: GOLD }} strokeWidth={1.5} />
                    <blockquote className="font-display italic text-lg leading-relaxed text-slate-100 flex-1">{it.quote}</blockquote>
                    <div className="h-px w-full my-6" style={{ backgroundColor: "rgba(212,175,55,0.3)" }} />
                    <figcaption className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0"
                        style={{ backgroundColor: "rgba(212,175,55,0.14)", color: GOLD, border: `1px solid ${GOLD}` }}>
                        {initials(it.name)}
                      </div>
                      <div>
                        <p className="font-semibold text-white text-sm">{it.name}</p>
                        <p className="text-sm text-slate-400">{it.role}</p>
                      </div>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <LatestArticleBar company="business" />

      <FaqBlock items={HOME_FAQ} heading="Spørgsmål vi får hver uge" tone="slate" />

      <BusinessLeadForm t={content.form} id="kontakt" />

      <CtaBlock title={content.finalCta.title} button={content.finalCta.button} to="/business/kontakt" />
      </main>

      <section className="py-14 sm:py-20 bg-white" data-testid="business-vognmand-onboarding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl p-8 sm:p-12 text-white" style={{ backgroundColor: "#0A192F" }}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: "#C8A14A" }}>
              Fra drøm til drift
            </p>
            <h2 className="mt-3 text-base md:text-lg font-semibold">
              Vognmand Onboarding, beregn din indtjening, før du investerer
            </h2>
            <p className="mt-3 text-sm text-slate-300 max-w-2xl">
              Seks trin med dine egne tal: arbejdsuge, omkostninger, kørselsaftaler og break-even.
              Du får en personlig vognmandsanalyse og kan hente den som PDF.
            </p>
            <Link to="/vognmand-onboarding" data-testid="business-vognmand-onboarding-link"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold"
              style={{ backgroundColor: "#C8A14A", color: "#0A192F" }}>
              Lav din vognmandsplan
            </Link>
          </div>
        </div>
      </section>

      <BusinessFooter tagline={content.footer.tagline} />
    </div>
  );
};

export default BusinessPage;
